import React from "react";

import {
  isOverdueQueue,
  isStaleQueue,
  type Operations,
  type OperationsMetric,
  type OperationsQueue,
  type OperationsWeek,
  STALE_QUEUE_HINT,
} from "@/api/warehouse_dashboard";
import { fmtDateTime, ruNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  fmtAge,
  fmtOperationValue,
  fmtWeekRange,
  groupMetrics,
  NO_VALUE_HINT,
  weekCaption,
} from "@/lib/warehouse-operations";

import { Sparkline } from "./Sparkline";

const NUM = "whitespace-nowrap px-2 py-1 text-right tabular-nums";
/** Липкая первая колонка: фон обязателен (иначе под ней видны числа), граница —
 *  `inset`-тенью (`border` при `border-collapse` уезжает вместе со скроллом). */
const STICKY = "sticky left-0 z-10 shadow-[inset_-1px_0_0_theme(colors.slate.200)]";

/**
 * Вкладка «Операции»: очереди «сейчас» полосой сверху и таблица «показатель ×
 * неделя» по группам. Всё — как пришло с бэкенда (`operations.py`): порядок
 * групп и строк, `0` против `null`, текущая неполная неделя. Дельт и оценок
 * «лучше/хуже» экран не считает — вместо дельты спарклайн по тем же `values`.
 */
export function OperationsTab({ data }: { data: Operations }): React.ReactElement {
  const groups = React.useMemo(() => groupMetrics(data.metrics), [data.metrics]);
  const colCount = data.weeks.length + 2;

  return (
    <div className="space-y-3">
      <QueueStrip queues={data.queues} asOf={data.as_of} />

      {/* Скролл — у таблицы, не у страницы; название показателя липнет слева. */}
      <div className="max-w-[1040px] overflow-x-auto rounded-lg border bg-white">
        <table className="w-full min-w-[920px] table-fixed text-[13px] leading-5">
          <colgroup>
            <col />
            {data.weeks.map((w) => (
              <col key={w.start} className="w-[78px]" />
            ))}
            <col className="w-[96px]" />
          </colgroup>
          <thead className="border-b bg-slate-50 text-xs text-muted-foreground">
            <tr className="text-left">
              <th className={cn(STICKY, "bg-slate-50 px-2 py-1.5 pl-3 font-medium")}>Показатель</th>
              {data.weeks.map((w) => (
                <WeekTh key={w.start} week={w} />
              ))}
              <th className="px-2 py-1.5 pr-3 text-center font-medium" title="Тренд по неделям">
                Тренд
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.group}>
                <tr className="border-b bg-slate-50/80">
                  <th
                    scope="rowgroup"
                    className={cn(
                      STICKY,
                      "bg-slate-50 px-2 pb-0.5 pl-3 pt-2 text-left text-xs font-semibold text-slate-700",
                    )}
                  >
                    {g.title}
                  </th>
                  <td colSpan={colCount - 1} />
                </tr>
                {g.metrics.map((m) => (
                  <MetricRow key={m.key} metric={m} weeks={data.weeks} />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {data.notes.length > 0 && (
        <ul className="max-w-[1040px] space-y-0.5 text-xs leading-5 text-muted-foreground">
          {data.notes.map((n) => (
            <li key={n}>* {n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Колонка недели: номер ISO-недели крупнее, диапазон дат мелко под ним —
 *  чтобы «по дням или по неделям?» не возникало. Подпись — `weekCaption`. */
function WeekTh({ week }: { week: OperationsWeek }): React.ReactElement {
  const c = weekCaption(week);
  return (
    <th
      className={cn("whitespace-nowrap px-2 py-1 text-right font-medium", week.partial && "italic")}
      title={`${fmtWeekRange(week.start, week.end)}${week.partial ? " — текущая, неполная" : ""}`}
    >
      <span className="block text-[13px] leading-4 text-slate-700">{c.title}</span>
      <span className="block text-[10px] font-normal leading-3">{c.range}</span>
    </th>
  );
}

function MetricRow({
  metric,
  weeks,
}: {
  metric: OperationsMetric;
  weeks: OperationsWeek[];
}): React.ReactElement {
  return (
    <tr className="group border-b last:border-b-0 hover:bg-slate-50">
      <td
        className={cn(STICKY, "truncate bg-white px-2 py-1 pl-3 group-hover:bg-slate-50")}
        title={metric.title}
      >
        {metric.title}
      </td>
      {metric.values.map((v, i) => {
        const partial = weeks[i]?.partial;
        return (
          <td
            key={weeks[i]?.start ?? i}
            className={cn(
              NUM,
              partial && "italic text-muted-foreground",
              v === null && "text-muted-foreground",
            )}
            title={v === null ? NO_VALUE_HINT : undefined}
          >
            {fmtOperationValue(v, metric.unit)}
          </td>
        );
      })}
      <td className="px-2 py-1 pr-3">
        <Sparkline values={metric.values} weeks={weeks} />
      </td>
    </tr>
  );
}

/** Очереди «сейчас» — полоса компактных карточек. Ненулевая просрочка —
 *  красным акцентом, ненулевые «зависшие» — янтарём с подписью порога, нулевые
 *  очереди приглушены, но НЕ спрятаны: «0» — это ответ, и пропавшая карточка
 *  читалась бы как «не считается». */
function QueueStrip({
  queues,
  asOf,
}: {
  queues: OperationsQueue[];
  asOf: string;
}): React.ReactElement {
  return (
    <section aria-label="Очереди сейчас" className="flex flex-wrap gap-2">
      {queues.map((q) => {
        const alarm = q.count > 0 && isOverdueQueue(q.key);
        const stale = isStaleQueue(q.key);
        const staleAlarm = stale && q.count > 0;
        const empty = q.count === 0;
        return (
          <div
            key={q.key}
            data-testid={`queue-${q.key}`}
            data-alarm={alarm || undefined}
            data-stale={staleAlarm || undefined}
            className={cn(
              "min-w-[160px] flex-1 rounded-lg border px-3 py-1.5 sm:max-w-[240px] sm:flex-none",
              alarm ? "border-rose-300 bg-rose-50" : staleAlarm ? "border-amber-200 bg-amber-50/60" : "bg-white",
              empty && "text-muted-foreground",
            )}
          >
            <div
              className={cn(
                "text-xs leading-4",
                alarm ? "text-rose-800" : staleAlarm ? "text-amber-900" : "text-muted-foreground",
              )}
            >
              {q.title}
              {stale && <span className="block text-[11px] leading-4 opacity-80">{STALE_QUEUE_HINT}</span>}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span
                className={cn(
                  "text-lg font-semibold tabular-nums",
                  alarm && "text-rose-700",
                  staleAlarm && "font-medium text-amber-800",
                  empty && "font-normal",
                )}
              >
                {ruNum.format(q.count)}
              </span>
              {q.count > 0 && (
                <span
                  className={cn("text-xs", alarm ? "text-rose-700" : "text-muted-foreground")}
                  title={q.oldest_at ? fmtDateTime(q.oldest_at) || q.oldest_at : undefined}
                >
                  {q.oldest_at
                    ? `старейшее: ${fmtAge(q.oldest_at, asOf)} назад`
                    : "старейшее: нет даты"}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
